/**
 * React-Hook, der die Sync-Engine ans UI anbindet.
 *
 * Trigger:
 *   1. Beim Mount: einmal sofort syncen, falls Token + online.
 *   2. Online-Event: sofort syncen.
 *   3. Outbox-Änderung (TanStack-Query-Invalidation): syncen wenn online.
 *   4. Manuell via `triggerSync()`.
 *
 * Liefert:
 *   - online (boolean)
 *   - pendingCount (Zahl der nicht-versandten Outbox-Items)
 *   - isSyncing (gerade dabei zu syncen?)
 *   - lastSync (Timestamp ms, oder null)
 *   - lastResult (letzte SyncResult, falls vorhanden)
 *   - triggerSync() (manuell auslösen)
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../store/auth';
import { db } from '../db/schema';
import { runSync, getLastSync, type SyncResult } from '../db/sync';

export interface SyncState {
  online: boolean;
  pendingCount: number;
  isSyncing: boolean;
  lastSync: number | null;
  lastResult: SyncResult | null;
  triggerSync: () => Promise<void>;
}

export function useSync(): SyncState {
  const token = useAuth((s) => s.token);
  // Gesamtprüfung 11.09.2026: nach einem Firmenwechsel neu synchronisieren
  // (lokale Standorte/Schichten gehören sonst noch zur vorigen Firma).
  const activeCompanyId = useAuth((s) => s.activeCompanyId);
  const qc = useQueryClient();
  const [online, setOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSync, setLastSync] = useState<number | null>(null);
  const [lastResult, setLastResult] = useState<SyncResult | null>(null);

  const pendingCount = useLiveQuery(() => db.outbox.count(), [], 0);
  // Gesamtprüfung 11.09.2026: nur Einträge, die ein erneuter Versuch auch
  // senden würde (keine Konflikte, keine Einträge einer anderen Firma) —
  // steuert den Wiederholungs-Timer, damit er nicht endlos läuft.
  const retryableCount = useLiveQuery(
    () => db.outbox.filter((e) => !e.conflict && (!e.companyId || e.companyId === activeCompanyId)).count(),
    [activeCompanyId],
    0,
  );

  // Online/Offline-Listener
  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Letzten Sync-Timestamp aus IndexedDB lesen.
  useEffect(() => {
    void getLastSync().then(setLastSync);
  }, []);

  // Verhindern, dass mehrere Sync-Vorgänge parallel laufen.
  const inFlight = useRef(false);
  // Gesamtprüfung 11.09.2026: kam während eines laufenden Syncs eine neue
  // Änderung dazu, wird danach direkt noch einmal synchronisiert (vorher
  // blieb sie liegen, bis zufällig der nächste Auslöser kam).
  const rerun = useRef(false);

  const doSync = useCallback(async () => {
    if (!token || !navigator.onLine) return;
    if (inFlight.current) {
      rerun.current = true;
      return;
    }
    inFlight.current = true;
    rerun.current = false;
    setIsSyncing(true);
    try {
      const result = await runSync(token);
      setLastResult(result);
      if (result.pulled) {
        const ts = await getLastSync();
        setLastSync(ts);
      }
      // Caches der React-Query-Hooks invalidieren, damit konsumierende
      // Komponenten neu rendern (auch wenn sie nicht direkt auf Dexie hören).
      qc.invalidateQueries();
    } catch (err) {
      console.error('[sync] runSync warf:', err);
    } finally {
      setIsSyncing(false);
      inFlight.current = false;
      if (rerun.current) {
        rerun.current = false;
        setTimeout(() => void doSyncRef.current(), 0);
      }
    }
  }, [token, qc]);
  const doSyncRef = useRef(doSync);
  doSyncRef.current = doSync;

  // Beim Mount + bei Online-Werden + bei Outbox-Änderung + Firmenwechsel
  useEffect(() => {
    void doSync();
  }, [doSync, online, pendingCount, activeCompanyId]);

  // Gesamtprüfung 11.09.2026: fehlgeschlagene Sendeversuche (Netzfehler,
  // 5xx, 429) regelmäßig wiederholen, solange etwas aussteht.
  useEffect(() => {
    if (!token || !online || !retryableCount) return;
    const id = setInterval(() => void doSyncRef.current(), 45_000);
    return () => clearInterval(id);
  }, [token, online, retryableCount]);

  // Shadow-Report 19.09.2026: Auf einem Tablet, das tagelang offen bleibt, lief
  // gar kein Abgleich mehr — Server-Änderungen (z. B. „erledigt“ nach
  // Schichtende oder neue Freigaben) kamen erst nach einem Neuladen an.
  // Deshalb: alle 3 Minuten und immer, wenn die App wieder in den Vordergrund
  // kommt bzw. das Fenster den Fokus bekommt.
  useEffect(() => {
    if (!token) return;
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') void doSyncRef.current();
    }, 3 * 60_000);
    const onWake = () => {
      if (document.visibilityState === 'visible') void doSyncRef.current();
    };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, [token]);

  return {
    online,
    pendingCount: pendingCount ?? 0,
    isSyncing,
    lastSync,
    lastResult,
    triggerSync: doSync,
  };
}
